import type { ContractSuretyType } from "@inrev/common";
import {
	ContractDamagesField,
	ContractHazardsField,
} from "@portal/components/features/contract-project";
import { FormComponent, FormFieldGroup, FormRow, FormSection } from "@portal/components/form";
import {
	FormDateComponent,
	FormInputComponent,
	FormNumberComponent,
	FormStateDropdownComponent,
} from "@portal/components/form/fieldLayouts";
import { FormNAICSCodeSelect } from "@portal/components/form/fields";
import { useFormContext } from "react-hook-form";
import type { AdminImportBondViewFormData } from "../types";

type ProjectSectionProps = {
	isBid: boolean;
	isClosed: boolean;
	contractSuretyType: ContractSuretyType | "";
};

export const ProjectSection = ({ isBid, isClosed, contractSuretyType }: ProjectSectionProps) => {
	const formMethods = useFormContext<AdminImportBondViewFormData>();

	return (
		<FormSection header="Project">
			<FormFieldGroup condensed>
				{!isBid && (
					<FormRow>
						<FormDateComponent
							formMethods={formMethods}
							name="bond.data.contractDate"
							label="Contract Date"
							inline
						/>
						<FormDateComponent
							formMethods={formMethods}
							name="bond.data.expectedCompletionDate"
							label="Expected Completion Date"
							inline
						/>
						{isClosed && (
							<FormDateComponent
								formMethods={formMethods}
								name="bond.data.actualCompletionDate"
								label="Actual Completion Date"
								inline
							/>
						)}
					</FormRow>
				)}
				{contractSuretyType !== "bid_to_final" && (
					<FormFieldGroup condensed>
						<FormInputComponent
							formMethods={formMethods}
							name="bond.data.project.name"
							label="Project Name"
							inline
						/>
						<FormInputComponent
							formMethods={formMethods}
							name="bond.data.project.description"
							label="Description"
							inline
						/>
						<FormRow>
							<FormInputComponent
								formMethods={formMethods}
								name="bond.data.project.address.city"
								label="City"
								inline
							/>
							<FormStateDropdownComponent
								formMethods={formMethods}
								name="bond.data.project.address.state"
								label="State"
								inline
							/>
						</FormRow>
						<FormComponent
							formMethods={formMethods}
							name="bond.data.project.scopeOfWork"
							label="Scope of work"
							inline
						>
							<FormNAICSCodeSelect
								control={formMethods.control}
								name="bond.data.project.scopeOfWork"
								selectText="Select NAICS Codes"
								editText="Edit NAICS Codes"
								searchPlaceholder="Search scopes of work..."
								title="Select scopes of work"
							/>
						</FormComponent>
						<FormRow>
							<FormNumberComponent
								formMethods={formMethods}
								name="bond.data.project.monthsToComplete"
								label="Months To Complete"
								inline
								min={1}
							/>
							<FormNumberComponent
								formMethods={formMethods}
								name="bond.data.project.contractWarrantyMonths"
								label="Contract Warranty Months"
								inline
								min={0}
							/>
						</FormRow>
						<ContractDamagesField
							formMethods={formMethods}
							name="bond.data.project.contractDamages"
							label="Does the contract include any of the following damages?"
							subLabel="Check all that apply"
							inline
							className="bg-surface-light border border-bc-default"
						/>
						<ContractHazardsField
							formMethods={formMethods}
							name="bond.data.project.contractHazards"
							label="Do any of the following apply to the contract?"
							subLabel="Check all that apply"
							inline
							className="bg-surface-light border border-bc-default"
						/>
					</FormFieldGroup>
				)}
			</FormFieldGroup>
		</FormSection>
	);
};
